import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Clock, TrendingUp, UserCheck, Settings, FileDown, Calendar, LogOut } from "lucide-react";
import axios from "axios";
import { format } from "date-fns";
import { de } from "date-fns/locale";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (!userData || JSON.parse(userData).type !== "admin") {
      navigate("/admin");
      return;
    }
    loadStats();
    const interval = setInterval(loadStats, 10000); // Refresh every 10 seconds
    return () => clearInterval(interval);
  }, [navigate]);

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error loading stats:", error);
      setLoading(false);
    }
  };

  const formatHours = (hours) => {
    const totalMinutes = Math.round(hours * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${h}h ${m}m`;
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/admin");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <p className="text-lg">Lädt...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      <div className="max-w-7xl mx-auto p-4 md:p-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            Admin Dashboard
          </h1>
          <Button
            variant="outline"
            onClick={handleLogout}
            className="rounded-full"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Abmelden
          </Button>
        </div>

        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card className="shadow-lg rounded-3xl border-0 bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90 mb-1">Mitarbeiter</p>
                  <p className="text-4xl font-bold" data-testid="total-employees">{stats?.total_employees || 0}</p>
                </div>
                <Users className="w-12 h-12 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg rounded-3xl border-0 bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90 mb-1">Eingestempelt</p>
                  <p className="text-4xl font-bold" data-testid="clocked-in-count">{stats?.currently_clocked_in?.length || 0}</p>
                </div>
                <UserCheck className="w-12 h-12 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg rounded-3xl border-0 bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90 mb-1">Heute</p>
                  <p className="text-3xl font-bold" data-testid="today-hours">{formatHours(stats?.today_hours || 0)}</p>
                </div>
                <Clock className="w-12 h-12 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg rounded-3xl border-0 bg-gradient-to-br from-orange-500 to-orange-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90 mb-1">Diese Woche</p>
                  <p className="text-3xl font-bold" data-testid="week-hours">{formatHours(stats?.week_hours || 0)}</p>
                </div>
                <TrendingUp className="w-12 h-12 opacity-80" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <Card className="shadow-xl rounded-3xl border-0">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserCheck className="w-6 h-6 text-green-600" />
                Aktuell Eingestempelt
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.currently_clocked_in?.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Niemand ist eingestempelt</p>
              ) : (
                <div className="space-y-3">
                  {stats?.currently_clocked_in?.map((entry) => (
                    <div key={entry.id} className="flex justify-between items-center p-4 bg-green-50 rounded-2xl" data-testid={`clocked-in-${entry.employee_id}`}>
                      <div>
                        <p className="font-semibold">{entry.employee_name}</p>
                        <p className="text-sm text-gray-600">
                          Seit {format(new Date(entry.clock_in), "HH:mm", { locale: de })} Uhr
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">
                          {Math.floor((new Date() - new Date(entry.clock_in)) / 1000 / 60 / 60)}h{' '}
                          {Math.floor(((new Date() - new Date(entry.clock_in)) / 1000 / 60) % 60)}m
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="shadow-xl rounded-3xl border-0">
            <CardHeader>
              <CardTitle>Schnellzugriff</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                onClick={() => navigate("/admin/employees")}
                className="w-full justify-start h-14 rounded-2xl bg-white hover:bg-gray-50 text-gray-800 border-2 border-gray-200 transition-colors duration-200"
                data-testid="manage-employees-button"
              >
                <Users className="w-5 h-5 mr-3" />
                Mitarbeiter verwalten
              </Button>
              <Button
                onClick={() => navigate("/admin/time-entries")}
                className="w-full justify-start h-14 rounded-2xl bg-white hover:bg-gray-50 text-gray-800 border-2 border-gray-200 transition-colors duration-200"
                data-testid="manage-time-entries-button"
              >
                <Clock className="w-5 h-5 mr-3" />
                Zeiteinträge bearbeiten
              </Button>
              <Button
                onClick={() => navigate("/admin/absences")}
                className="w-full justify-start h-14 rounded-2xl bg-white hover:bg-gray-50 text-gray-800 border-2 border-gray-200 transition-colors duration-200"
                data-testid="manage-absences-button"
              >
                <Calendar className="w-5 h-5 mr-3" />
                Abwesenheiten verwalten
              </Button>
              <Button
                onClick={() => window.open(`${API}/export/xlsx`, '_blank')}
                className="w-full justify-start h-14 rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white transition-colors duration-200"
                data-testid="export-button"
              >
                <FileDown className="w-5 h-5 mr-3" />
                Excel Export
              </Button>
              <Button
                onClick={() => navigate("/admin/settings")}
                className="w-full justify-start h-14 rounded-2xl bg-white hover:bg-gray-50 text-gray-800 border-2 border-gray-200 transition-colors duration-200"
                data-testid="settings-button"
              >
                <Settings className="w-5 h-5 mr-3" />
                Einstellungen
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
